import React, { useCallback, useMemo, useState } from "react";
import {
    Box, Button, Table, TableBody, TableCell,
    TableFooter, TableHead, TableRow, TablePagination
} from "@material-ui/core";
import { ITable, TableWager } from "../../../interfaces";
import { useCancelWagerMutation, useFindWagersQuery } from "../../../api/wager";
import { WagerHistoryDialog } from "./wager-history-dialog";

function getWagerType(wager: TableWager): string {
    const type = wager.meta_data?.type;
    if (type) {
        switch (type) {
            case "fortune_wheel_freespin": return "Колесо фортуны (фриспины)";
            case "fortune_wheel": return "Колесо фортуны";
            case "cashback": return "Кэшбэк";
            default: return type;
        }
    }
    return wager.meta_data?.num ? `Пополнение №${wager.meta_data?.num}` : "неизвестно";
}

interface WagersProps {
    table: ITable;
}

const WagersView: React.FC<WagersProps> = ({ table }) => {
    // Material UI использует 0-индексацию для страниц, ставим 0 по умолчанию
    const [page, setPage] = useState(0);

    // Передаем page + 1, так как Yii2 начинает пагинацию с 1
    const { data } = useFindWagersQuery({
        hall_id: table.hall_id,
        id: table.id,
        page: page + 1
    });

    // Стейты для модального окна истории
    const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
    const [selectedWagerId, setSelectedWagerId] = useState<number | null>(null);

    const [cancel, { isLoading: isCanceling }] = useCancelWagerMutation();

    // Извлекаем данные и мету с фоллбеками на случай отсутствия данных
    const wagers = useMemo(() => data?.items ?? null, [data]);
    const meta = useMemo(() => data?.meta || { totalCount: 0, perPage: 20 }, [data]);

    const handleChangePage = useCallback((_event: unknown, newPage: number) => {
        setPage(newPage);
    }, []);

    const handleOpenHistory = useCallback((wagerId: number) => {
        setSelectedWagerId(wagerId);
        setHistoryDialogOpen(true);
    }, []);

    const handleCloseHistory = useCallback(() => {
        setHistoryDialogOpen(false);
        // Сбрасываем ID с небольшой задержкой, чтобы диалог успел красиво закрыться без скачков данных
        setTimeout(() => setSelectedWagerId(null), 300);
    }, []);

    if (!data || !wagers) return null;

    return (
        <Box display="flex" flexDirection="column" justifyContent="space-between" style={{ width: "100%", overflowX: "auto" }}>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell></TableCell>
                        <TableCell colSpan={2} style={{ textAlign: "center", backgroundColor: "#e6e6e6" }}>Обязательный</TableCell>
                        <TableCell colSpan={3} style={{ textAlign: "center", backgroundColor: "#cfcfcf" }}>бонус</TableCell>
                        <TableCell colSpan={3}></TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell>Сумма</TableCell>
                        <TableCell style={{ backgroundColor: "#e6e6e6" }}>Отыграно</TableCell>
                        <TableCell style={{ backgroundColor: "#e6e6e6" }}>Нужно отыграть</TableCell>
                        <TableCell style={{ backgroundColor: "#cfcfcf" }}>Бонус</TableCell>
                        <TableCell style={{ backgroundColor: "#cfcfcf" }}>Отыграно</TableCell>
                        <TableCell style={{ backgroundColor: "#cfcfcf" }}>Осталось отыграть</TableCell>
                        <TableCell>Бонус активен</TableCell>
                        <TableCell>Источник</TableCell>
                        <TableCell>Действия</TableCell>
                    </TableRow>
                </TableHead>

                {wagers.length === 0 && (
                    <TableFooter>
                        <TableRow>
                            <TableCell align="center" colSpan={9}>Нет Вэйджеров</TableCell>
                        </TableRow>
                    </TableFooter>
                )}

                {wagers.length > 0 && (
                    <TableBody>
                        {wagers.map(wager => (
                            <TableRow key={wager.id}>
                                <TableCell>{wager.amount / 100}</TableCell>
                                <TableCell style={{ backgroundColor: "#e6e6e6" }}>{wager.completed_amount / 100}</TableCell>
                                <TableCell style={{ backgroundColor: "#e6e6e6" }}>{wager.total_amount / 100}</TableCell>
                                <TableCell style={{ backgroundColor: "#cfcfcf" }}>{wager.bonus / 100}</TableCell>
                                <TableCell style={{ backgroundColor: "#cfcfcf" }}>{wager.bonus_completed_amount / 100}</TableCell>
                                <TableCell style={{ backgroundColor: "#cfcfcf" }}>{wager.bonus_total_amount / 100}</TableCell>
                                <TableCell>{wager.is_bonus_active ? "Да" : "Нет"}</TableCell>
                                <TableCell>{getWagerType(wager)}</TableCell>
                                <TableCell>
                                    <Button
                                        disabled={isCanceling}
                                        size="small"
                                        color="primary"
                                        variant="contained"
                                        onClick={() => cancel({ hall_id: table.hall_id, id: wager.id })}
                                    >
                                        Аннулировать
                                    </Button>
                                    <Button
                                        size="small"
                                        color="default"
                                        variant="outlined"
                                        onClick={() => handleOpenHistory(wager.id)}
                                    >
                                        История ставок
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                )}
            </Table>

            {/* Добавляем блок пагинации */}
            {meta.totalCount > 0 && (
                <TablePagination
                    component="div"
                    count={meta.totalCount}      // Общее количество элементов с бэка
                    page={page}                  // Текущая страница в стейте React (от 0)
                    onPageChange={handleChangePage}
                    rowsPerPage={meta.perPage}   // Лимит элементов на страницу
                    rowsPerPageOptions={[meta.perPage]} // Убираем возможность менять лимит (если нужно, можно добавить)
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} из ${count}`}
                />
            )}
            <WagerHistoryDialog
                open={historyDialogOpen}
                onClose={handleCloseHistory}
                hallId={table.hall_id}
                wagerId={selectedWagerId}
            />
        </Box>
    );
};

export default WagersView;